import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Bot,
  BrainCircuit,
  CalendarClock,
  Cloud,
  Code2,
  Cpu,
  Database,
  FileCode2,
  GitBranch,
  HardDrive,
  Layers3,
  Network,
  Play,
  Share2,
  Sparkles,
  TrendingUp,
  User,
  Wand2,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useSubscriptionAccess } from '../../../context/SubscriptionAccessContext';
import { AI_ROUTE_SURFACES, APP_ROUTE_SURFACES } from '../../../access/surfaces';

type Surface =
  | (typeof AI_ROUTE_SURFACES)[keyof typeof AI_ROUTE_SURFACES]
  | (typeof APP_ROUTE_SURFACES)[keyof typeof APP_ROUTE_SURFACES];

type HubItem = {
  label: string;
  path: string;
  description: string;
  icon: LucideIcon;
  surface?: Surface;
  surfaces?: Surface[];
};

type HubSection = {
  title: string;
  description: string;
  icon: LucideIcon;
  accent: string;
  items: HubItem[];
};

type HubPageConfig = {
  eyebrow: string;
  title: string;
  description: string;
  helper: string[];
  sections: HubSection[];
};

const ASSET_ITEMS: HubItem[] = [
  {
    label: 'Agent Skills',
    path: '/ai/skills',
    description: 'Create reusable agent skills or copy public skill templates.',
    icon: Sparkles,
    surface: AI_ROUTE_SURFACES.agents,
  },
  {
    label: 'Prompt Snippets',
    path: '/ai/snippets',
    description: 'Reuse prompt fragments and structured prompt patterns.',
    icon: FileCode2,
    surface: AI_ROUTE_SURFACES.snippets,
  },
  {
    label: 'Indicator Presets',
    path: '/ai/indicator-sets',
    description: 'Maintain reusable indicator bundles for systems and experts.',
    icon: TrendingUp,
    surface: AI_ROUTE_SURFACES.indicatorSets,
  },
  {
    label: 'Global Storage',
    path: '/ai/global-storage',
    description: 'Keep shared files, text, and assets available across systems.',
    icon: HardDrive,
    surface: AI_ROUTE_SURFACES.globalStorage,
  },
  {
    label: 'Expert Library',
    path: '/ai/experts',
    description: 'Audit experts that already exist inside AI systems.',
    icon: User,
    surface: AI_ROUTE_SURFACES.experts,
  },
  {
    label: 'Schedules',
    path: '/ai/schedules',
    description: 'Review and manage schedules across AI systems.',
    icon: CalendarClock,
    surface: AI_ROUTE_SURFACES.schedules,
  },
];

const AUTOMATION_PAGE: HubPageConfig = {
  eyebrow: 'Automation',
  title: 'Choose an automation workspace',
  description:
    'Start from AI systems, trading bots, agents, copy trading, execution insights, or reusable automation assets.',
  helper: [
    'Use AI systems for decision workflows.',
    'Use trading bots for strategy execution.',
    'Use agents and copy trading for autonomous and account-to-account workflows.',
    'Use asset shortcuts for shared prompts, presets, storage, and experts.',
  ],
  sections: [
    {
      title: 'Core Automation',
      description: 'Core automation surfaces for building, running, and reviewing automated trading workflows.',
      icon: BrainCircuit,
      accent: 'from-amber-500/15 via-amber-500/5 to-transparent',
      items: [
        {
          label: 'AI Systems',
          path: '/ai/systems',
          description: 'Create and manage AI-driven trading systems.',
          icon: BrainCircuit,
          surface: AI_ROUTE_SURFACES.systems,
        },
        {
          label: 'Trading Bots',
          path: '/bots',
          description: 'Manage automated strategy bots and live runs.',
          icon: Bot,
          surface: APP_ROUTE_SURFACES.botsList,
        },
        {
          label: 'Agents',
          path: '/ai/agents',
          description: 'Manage autonomous agents, tools, and agent schedules.',
          icon: Wand2,
          surface: AI_ROUTE_SURFACES.agents,
        },
        {
          label: 'Agent Memory',
          path: '/ai/agent-memory',
          description: 'Inspect durable agent memories, provenance, and relationship graphs.',
          icon: Network,
          surface: AI_ROUTE_SURFACES.agents,
        },
        {
          label: 'Copy Trading',
          path: '/copy-trading',
          description: 'Manage provider, follower, and allocation automation.',
          icon: Share2,
          surface: APP_ROUTE_SURFACES.copyTrading,
        },
        {
          label: 'Insights',
          path: '/ai/analytics?tab=runs',
          description: 'Review AI runs, executions, usage, and automation analytics.',
          icon: BarChart3,
          surface: AI_ROUTE_SURFACES.analytics,
        },
      ],
    },
    {
      title: 'Automation Assets',
      description: 'Shared skills, prompts, indicators, storage, schedules, and expert-library assets used by automation workflows.',
      icon: Wrench,
      accent: 'from-emerald-500/15 via-emerald-500/5 to-transparent',
      items: ASSET_ITEMS,
    },
  ],
};

const MODEL_LAB_PAGE: HubPageConfig = {
  eyebrow: 'Model Lab',
  title: 'Choose the model workflow you need',
  description:
    'Start from datasets, features, model registry, training, experiments, prediction tests, or deployment management.',
  helper: [
    'Use datasets and features to prepare training inputs.',
    'Use models and versions to manage the registry.',
    'Use training, experiments, prediction tests, and deployments to validate and ship models.',
  ],
  sections: [
    {
      title: 'Build',
      description: 'Prepare data, engineer features, and manage registered model assets.',
      icon: GitBranch,
      accent: 'from-sky-500/15 via-sky-500/5 to-transparent',
      items: [
        {
          label: 'Datasets',
          path: '/ai/ml/datasets',
          description: 'Import and version training data sources.',
          icon: Database,
          surface: AI_ROUTE_SURFACES.mlDatasets,
        },
        {
          label: 'Features',
          path: '/ai/ml/features',
          description: 'Build feature engineering pipelines and target definitions.',
          icon: TrendingUp,
          surface: AI_ROUTE_SURFACES.mlFeatures,
        },
        {
          label: 'Models',
          path: '/ai/ml/models',
          description: 'Manage model assets and open the model builder.',
          icon: Bot,
          surface: AI_ROUTE_SURFACES.mlModels,
        },
        {
          label: 'Model Versions',
          path: '/ai/ml/model-versions',
          description: 'Inspect model version history and registry lineage.',
          icon: Layers3,
          surface: AI_ROUTE_SURFACES.mlModels,
        },
      ],
    },
    {
      title: 'Validate And Ship',
      description: 'Run training jobs, compare experiments, test predictions, and manage deployments.',
      icon: Play,
      accent: 'from-emerald-500/15 via-emerald-500/5 to-transparent',
      items: [
        {
          label: 'Training Jobs',
          path: '/ai/ml/training-jobs',
          description: 'Launch and monitor training executions.',
          icon: Play,
          surface: AI_ROUTE_SURFACES.mlTrainingJobs,
        },
        {
          label: 'Servers',
          path: '/ai/ml/servers',
          description: 'Manage remote compute specs, runs, runners, and on-demand servers.',
          icon: Cloud,
          surface: AI_ROUTE_SURFACES.mlServers,
        },
        {
          label: 'Experiments',
          path: '/ai/ml/experiments',
          description: 'Compare completed training runs and metrics.',
          icon: BarChart3,
          surface: AI_ROUTE_SURFACES.mlExperiments,
        },
        {
          label: 'Prediction Tests',
          path: '/ai/ml/prediction-tests',
          description: 'Replay predictions against symbols, timeframes, and history.',
          icon: BrainCircuit,
          surface: AI_ROUTE_SURFACES.mlPredictionTests,
        },
        {
          label: 'Deployments',
          path: '/ai/ml/deployments',
          description: 'Manage deployed model endpoints and live inference state.',
          icon: Sparkles,
          surface: AI_ROUTE_SURFACES.mlDeployments,
        },
      ],
    },
  ],
};

const ADMIN_PAGE: HubPageConfig = {
  eyebrow: 'Admin',
  title: 'Manage platform AI configuration',
  description:
    'Choose the provider, model, embedding, or service-instruction surface you need to administer.',
  helper: [
    'Use providers for credentials and availability.',
    'Use models and embeddings for runtime inventory.',
    'Use service instructions for shared platform-level behavior.',
  ],
  sections: [
    {
      title: 'Model And Service Administration',
      description: 'Platform-level AI provider, model, embedding, and instruction settings.',
      icon: Cloud,
      accent: 'from-violet-500/15 via-violet-500/5 to-transparent',
      items: [
        {
          label: 'LLM Providers',
          path: '/ai/providers',
          description: 'Configure provider credentials and availability.',
          icon: Cloud,
          surface: AI_ROUTE_SURFACES.providers,
        },
        {
          label: 'LLM Models',
          path: '/ai/models',
          description: 'Manage model inventory and routing options.',
          icon: Cpu,
          surface: AI_ROUTE_SURFACES.models,
        },
        {
          label: 'Embedding Models',
          path: '/ai/embedding-models',
          description: 'Manage embedding model inventory and availability.',
          icon: Sparkles,
          surface: AI_ROUTE_SURFACES.embeddingModels,
        },
        {
          label: 'Service Instructions',
          path: '/ai/service-instructions',
          description: 'Maintain shared service-level instruction sets.',
          icon: Code2,
          surface: AI_ROUTE_SURFACES.serviceInstructions,
        },
      ],
    },
  ],
};

function HubSectionCard({ section, items, onNavigate }: { section: HubSection; items: HubItem[]; onNavigate: (path: string) => void }) {
  const Icon = section.icon;

  return (
    <div className="overflow-hidden rounded-xl border border-borderDefault bg-backgroundSurface">
      <div className={`border-b border-borderDefault bg-gradient-to-br ${section.accent} p-5`}>
        <div className="flex items-center gap-3">
          <div className="rounded-xl border border-borderDefault bg-backgroundSurface/90 p-2">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-textPrimary">{section.title}</h2>
            <p className="mt-1 text-sm text-textSecondary">{section.description}</p>
          </div>
        </div>
      </div>

      <div className="space-y-3 p-4">
        {items.map((item) => {
          const ItemIcon = item.icon;

          return (
            <button
              key={item.path}
              type="button"
              onClick={() => onNavigate(item.path)}
              className="group w-full rounded-xl border border-borderDefault bg-backgroundSurface p-4 text-left transition hover:border-primary/40 hover:bg-primary/5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg border border-borderDefault bg-backgroundInput p-2">
                    <ItemIcon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-textPrimary transition-colors group-hover:text-primary">
                      {item.label}
                    </p>
                    <p className="mt-1 text-sm text-textSecondary">{item.description}</p>
                  </div>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-textSecondary transition-colors group-hover:text-primary" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AISectionHubPage({ config }: { config: HubPageConfig }) {
  const navigate = useNavigate();
  const { canAccessSurface } = useSubscriptionAccess();

  const canAccessItem = (item: HubItem) => {
    const surfaces = item.surfaces ?? (item.surface ? [item.surface] : []);
    return surfaces.length === 0 || surfaces.some((surface) => canAccessSurface(surface).allowed);
  };

  const visibleSections = config.sections
    .map((section) => ({
      ...section,
      items: section.items.filter(canAccessItem),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <div className="mx-auto my-2 max-w-7xl space-y-4 sm:my-4 md:my-6">
      <div className="relative overflow-hidden rounded-2xl border border-borderDefault bg-gradient-to-br from-primary/10 via-backgroundSurface to-backgroundSurface p-6 sm:p-8">
        <div className="relative z-10 flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary/80">{config.eyebrow}</p>
            <h1 className="mt-2 text-3xl font-bold text-textPrimary sm:text-4xl">{config.title}</h1>
            <p className="mt-3 text-sm leading-relaxed text-textSecondary sm:text-base">{config.description}</p>
          </div>

          <div className="rounded-2xl border border-borderDefault bg-backgroundSurface/80 p-4 sm:p-5 xl:max-w-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-textSecondary">Where To Start</p>
            <div className="mt-3 space-y-2 text-sm text-textSecondary">
              {config.helper.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </div>
        </div>
      </div>

      {visibleSections.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {visibleSections.map((section) => (
            <HubSectionCard key={section.title} section={section} items={section.items} onNavigate={navigate} />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-borderDefault bg-backgroundSurface p-6 text-sm text-textSecondary">
          No available destinations for your current access level.
        </div>
      )}
    </div>
  );
}

export function AutomationHubPage() {
  return <AISectionHubPage config={AUTOMATION_PAGE} />;
}

export function ModelLabHubPage() {
  return <AISectionHubPage config={MODEL_LAB_PAGE} />;
}

export function AIAdminHubPage() {
  return <AISectionHubPage config={ADMIN_PAGE} />;
}
